import {
  CollaboratorSchema,
  SignableAnnotationSchema,
  SignableSchema,
  SignablesJSONStructureItemSchema,
  SignablesJSONStructureSchema,
  SignablesSchema,
  SignerSchema,
} from '../../definitions/schemas';
import {
  COMPLETED_SIGNATURE_STATUS,
  getSignableStatus,
} from '../../enums/Signables';
import { SignerEntry } from './Save/types';
import { SignableFieldType } from '../../enums/SignableFieldType';

export const buildStructureSignable = (
  signable: SignableSchema
): SignablesJSONStructureItemSchema => ({
  id: signable.id,
  name: signable.name,
  visible: true,
});

/**
 * Ordering the signable documents based on the order from the structure.
 * Also applying overwrites to the name and visibility from the structure.
 */
export const orderSignables = (
  signables: SignablesSchema,
  signablesStructure: SignablesJSONStructureSchema
) => {
  const result: SignablesSchema = [];

  signablesStructure.forEach((structureSignable) => {
    const signable = signables.find(({ id }) => id === structureSignable.id);

    if (!signable) {
      return;
    }

    result.push({
      ...signable,
      name: structureSignable.name,
      visible: structureSignable.visible,
    });
  });

  return result;
};

/**
 * Classifies the given signers into those whom already completed their signatures
 * and those that are still pending to sign
 */
export const classifySignableSigners = (
  signers: SignerSchema[]
): [SignerSchema[], SignerSchema[], SignerSchema[]] => {
  const assigned: SignerSchema[] = [];
  const pending: SignerSchema[] = [];
  const signed: SignerSchema[] = [];

  signers.forEach((signer, index) => {
    const completed =
      signer.status && COMPLETED_SIGNATURE_STATUS.includes(signer.status);

    if (signer.email) {
      assigned.push(signer);
    }

    if (completed) {
      signed.push(signer);
      return;
    }

    if (signer.email || signer.name) {
      pending.push(signer);
      return;
    }

    pending.push({
      ...signer,
      name: `Signer ${index + 1}`,
    });
  });

  return [signed, pending, assigned];
};

export const buildSignerFromCollaborator = (
  collaborator: CollaboratorSchema | null
) => ({
  collaboratorId: collaborator?.id,
  company: collaborator?.company,
  email: collaborator?.email || '',
  jobTitle: collaborator?.jobTitle,
  name: `${collaborator?.firstName || ''} ${
    collaborator?.lastName || ''
  }`.trim(),
  photo: collaborator?.photo,
  type: collaborator?.type,
});

export const isSignerValid = (
  signer: SignerEntry,
  annotations: SignableAnnotationSchema[]
): boolean =>
  annotations.some(
    ({ signerId, type }) =>
      signerId === signer.id && type === SignableFieldType.Signature
  );

export const getSignerIndex = (signers: SignerSchema[], signer: SignerSchema) =>
  signers.findIndex(({ id }) => id === signer.id);

/**
 * If only 1 signer for doc, simplify to "Missing required field: Signature".
 * If 2+ signers is missing, we can use text "Missing required fields: Signature for Signer X, Signer Y".
 * If 2+ signers and only 1 missing, it'd be "Missing required field: Signature for Signer X"
 */
export const getTextWarningMessage = (
  signers: SignerSchema[],
  signersInvalid: SignerEntry[]
) => {
  const textSigners = signersInvalid.map(
    (signer) => `Signer ${getSignerIndex(signers, signer) + 1}`
  );

  if (signers.length > 1) {
    return `Missing required field: Signature for ${textSigners.join(', ')}`;
  }

  return 'Missing required field: Signature';
};

/**
 * Classifies signables based on whether they are completed or have any error
 * (status or sync status errors)
 */
export const classifySignablesByStatus = (
  signables: SignablesSchema
): [SignablesSchema, SignablesSchema] => {
  const completed: SignablesSchema = [];
  const withError: SignablesSchema = [];

  signables.forEach((signable) => {
    const [hasStatusError, hasSyncStatusError, isCompleted] = getSignableStatus(
      signable.status,
      signable.syncStatus
    );

    if (isCompleted) {
      completed.push(signable);
      return;
    }

    if (hasStatusError || hasSyncStatusError) {
      withError.push(signable);
    }
  });

  return [completed, withError];
};
